import React, { Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { useStore } from '../../store';
import Spinner, { SpinnerColor } from '../../generic-components/Spinner';
import { DEFAULT_VIEWS_BY_ROLE, ROUTES_MAP } from '../constants';
import { canUpgradeToUseFeature, validateFeatureFlag } from '../../services/featureFlagService';
import { requiredDataChecker } from './RequiredDataChecker';
import { ProfilePlanPromotion } from '../../flows/profile/components/ProfilePlanPromotion';
import { featureAccess, LimitedFeatureType } from '../../services/appAccess.service';
import { FeatureAccess } from '../../pages/AccountSettings/components/rolesAndPermissions/PermissionsList';
import { ISummaryData } from '../../types';

const { LOGIN } = ROUTES_MAP;

interface IProps {
  requiredProperty: string;
  validateData?: string;
  fallbackRoute?: string;
  Next: any;
  readyComponent?: boolean;
  featureFlagName?: string;
  minAccess?: FeatureAccess;
  limitedFeature?: LimitedFeatureType;
}

const hasAccess = (limitedFeature: LimitedFeatureType, minAccess: FeatureAccess) => {
  const userPermission = featureAccess(null, limitedFeature);

  switch (minAccess) {
    case FeatureAccess.READ_WRITE:
      return userPermission === FeatureAccess.READ_WRITE;
    case FeatureAccess.READ_ONLY:
      return userPermission === FeatureAccess.READ_ONLY || userPermission === FeatureAccess.READ_WRITE;
    default:
      return true;
  }
};

export interface RoleGuardedRoute {
  element: any;
  minAccess: FeatureAccess;
  limitedFeature: LimitedFeatureType;
}

export const PrivateRoutes = ({
  requiredProperty,
  fallbackRoute = LOGIN,
  Next,
  readyComponent,
  featureFlagName,
  validateData,
  minAccess = FeatureAccess.NONE,
  limitedFeature,
}: IProps) => {
  const getUserData = useStore((state) => state.currUser);
  const { customerId } = getUserData();
  const summaryData: ISummaryData = useStore((state) => state.summary);
  const isFeatureUnavailable =
    featureFlagName && !validateFeatureFlag(featureFlagName) && !canUpgradeToUseFeature(featureFlagName);
  const isRequiredDataInvalid = validateData && !requiredDataChecker(validateData);

  const isAuthRequired = requiredProperty === 'auth';
  const isAuthenticated = customerId && summaryData?.customer;
  if (isRequiredDataInvalid) {
    return <Navigate to={fallbackRoute} />;
  }
  if (isFeatureUnavailable) {
    return <Navigate to={fallbackRoute} />;
  }

  if (isAuthRequired) {
    if (!isAuthenticated) {
      return <Navigate to={fallbackRoute} />;
    }

    const { authorizedUserType } = summaryData;
    const isUserAccessRestricted = authorizedUserType && limitedFeature && !hasAccess(limitedFeature, minAccess);
    if (isUserAccessRestricted) {
      const defaultRoute = DEFAULT_VIEWS_BY_ROLE[authorizedUserType as keyof typeof DEFAULT_VIEWS_BY_ROLE];
      return <Navigate to={defaultRoute} />;
    }

    const canUpgradeToUse =
      featureFlagName && !validateFeatureFlag(featureFlagName) && canUpgradeToUseFeature(featureFlagName);
    if (canUpgradeToUse) {
      return (
        <Suspense fallback={<Spinner spinnerColor={SpinnerColor.Blue} height='2.375rem' width='2.375rem' />}>
          <ProfilePlanPromotion shouldImmediatelyInvoke planType={summaryData.customerProfileInfo.customerProfile} />
        </Suspense>
      );
    }
  }
  return !readyComponent ? <Next /> : Next;
};
